import { useMemo, useState } from 'react';
import { useAccountsCrumb } from './lib/usePhoneCrumb';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Chip, DataTable, Pane, PaneHead, PickerChip, TabBar } from '@/components/kit';
import type { TableRow } from '@/components/kit';
import apiClient from '@/services/apiClient';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { PeriodRow } from './components/PeriodRow';
import { exportRows } from './lib/exportCsv';
import {
  amount,
  periodLabel,
  thisPeriod,
  useAccountsSummary,
} from './lib/useAccounts';
import { AccountantMenu } from './AccountantMenu';

/**
 * Bills & claims — `g_bills` (2), `g_claims` (2b), `g_claimsaxa` (2c).
 * THE BILLING SUITE, PERMISSION-FLATTENED.
 *
 * The desk's own billing screens are the same records with verbs on them:
 * collect, refund, discount, void. This surface is the SAME LIST WITH THE
 * VERBS REMOVED — the accountant reads bills and never edits them, which is
 * what "permission-flattened" means on the board's own header and why the
 * pane's only action is to open the edit history somebody else wrote.
 *
 * A CLAIM'S CLOCK IS ITS CONTENT. The bills tab sorts by date because a bill
 * is settled or it is not; the claims tab sorts by how long the money has been
 * waiting, because a claim's whole problem is elapsed time against terms.
 */

type Tab = 'bills' | 'claims';

interface Invoice {
  id: string;
  invoiceNumber?: string;
  invoice_number?: string;
  date?: string;
  status?: string;
  total?: number | string;
  taxAmount?: number | string;
  tax_amount?: number | string;
  amountPaid?: number | string;
  amount_paid?: number | string;
  balance?: number | string;
  patient?: { firstName?: string; lastName?: string } | null;
}

/** One filed claim, from `GET /accounts/claims`. */
interface Claim {
  id: string;
  claimNumber: string;
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate?: string | null;
  insurer: string | null;
  amountMinor: number;
  paidMinor: number;
  outstandingMinor: number;
  status: string;
  filedAt: string | null;
  approvedAt: string | null;
  paidAt: string | null;
  termsDays: number;
  dueOn: string | null;
  daysToDue: number | null;
  remittanceRef: string | null;
  filedTo: string | null;
}

interface InvoiceDetail extends Invoice {
  items?: Array<{ id: string; description?: string; quantity?: number; amount?: number | string }>;
  paymentMethod?: string;
  issuedBy?: { firstName?: string; lastName?: string } | null;
  createdAt?: string;
  updatedAt?: string;
  dueDate?: string;
}

const unwrap = <T,>(body: unknown): T => ((body as { data?: T })?.data ?? body) as T;

/**
 * THE LIST ENDPOINTS CAP AT A HUNDRED, and a busy month is more than that —
 * 332 bills in the dev clinic's August alone. Asking for 200 does not
 * truncate, it is REFUSED outright, so the screen showed "it did not come
 * back" over a perfectly healthy ledger. Pages until the server stops filling
 * one, with a ceiling so a misconfigured endpoint cannot spin here forever.
 */
const PAGE = 100;
const MAX_PAGES = 12;

async function pageAll<T>(url: string, params: Record<string, unknown> = {}): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const res = await apiClient.get(url, { params: { ...params, limit: PAGE, page } });
    const batch = list<T>(res.data);
    out.push(...batch);
    if (batch.length < PAGE) break;
  }
  return out;
}
const num = (v: unknown): number => Math.round(Number(v ?? 0) * 100);
const list = <T,>(body: unknown): T[] => {
  const d = unwrap<T[] | { data?: T[]; items?: T[] }>(body);
  if (Array.isArray(d)) return d;
  return (d as { data?: T[]; items?: T[] })?.data ?? (d as { items?: T[] })?.items ?? [];
};

/** The desk's status words, in the accountant's chip colours. */
function statusChip(status: string | undefined) {
  const s = (status ?? '').toLowerCase();
  if (s === 'paid') return <Chip kind="success" small>Paid</Chip>;
  if (s === 'partially_paid') return <Chip kind="warn" small>Partial</Chip>;
  if (s === 'overdue') return <Chip kind="danger" small>Overdue</Chip>;
  if (s === 'cancelled') return <Chip kind="outline" small>Void</Chip>;
  if (s === 'draft') return <Chip kind="outline" small>Draft</Chip>;
  return <Chip kind="soft" small>Pending</Chip>;
}

export function BillsPage() {
  const [params, setParams] = useSearchParams();
  const [period, setPeriod] = useState(thisPeriod);
  const [sort, setSort] = useState<'newest' | 'largest' | 'owing'>('newest');
  const [insurer, setInsurer] = useState('');
  const [pickedId, setPicked] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const tab: Tab = params.get('tab') === 'claims' ? 'claims' : 'bills';
  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    if (next === 'claims') p.set('tab', 'claims');
    else p.delete('tab');
    setParams(p, { replace: true });
    setPicked(null);
  };

  usePageMeta({ title: 'Bills & claims' });
  const { data: summary } = useAccountsSummary(period);

  const bills = useQuery({
    queryKey: ['accountant', 'bills', period],
    staleTime: 60_000,
    retry: false,
    queryFn: () => pageAll<Invoice>('/billing'),
  });

  const claims = useQuery({
    queryKey: ['accountant', 'claims'],
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<Claim[]> => list<Claim>((await apiClient.get('/accounts/claims')).data),
  });

  const billRows = useMemo(() => {
    const [y, m] = period.split('-');
    const rows = (bills.data ?? []).filter((b) =>
      (b.date ?? '').startsWith(`${y}-${m}`),
    );
    const q = query.trim().toLowerCase();
    const found = q
      ? rows.filter((b) =>
          `${numberOf(b)} ${patientOf(b)} ${b.total ?? ''}`.toLowerCase().includes(q),
        )
      : rows;
    return [...found].sort((a, b) => {
      if (sort === 'largest') return num(b.total) - num(a.total);
      if (sort === 'owing') return num(b.balance) - num(a.balance);
      /* `date` is a DAY, not an instant, so a busy Tuesday's bills all tie on
         it and come back in whatever order the database felt like. The number
         breaks the tie, which is the order the clinic itself raised them in. */
      return (
        (b.date ?? '').localeCompare(a.date ?? '') ||
        numberOf(b).localeCompare(numberOf(a))
      );
    });
  }, [bills.data, period, sort, query]);

  const insurers = useMemo(
    () => [...new Set((claims.data ?? []).map(insurerOf).filter(Boolean))].sort(),
    [claims.data],
  );

  const claimRows = useMemo(() => {
    const rows = (claims.data ?? []).filter(
      (c) =>
        (!insurer || insurerOf(c) === insurer) &&
        (!query.trim() ||
          `${claimNumberOf(c)} ${c.invoiceNumber} ${insurerOf(c)} ${c.remittanceRef ?? ''}`
            .toLowerCase()
            .includes(query.trim().toLowerCase())),
    );
    /* The oldest money first: a claim's problem is elapsed time. Paid last. */
    return [...rows].sort(
      (a, b) =>
        Number(a.status === 'paid') - Number(b.status === 'paid') ||
        (a.daysToDue ?? 9999) - (b.daysToDue ?? 9999),
    );
  }, [claims.data, insurer, query]);

  const picked =
    tab === 'bills'
      ? billRows.find((b) => b.id === pickedId)
      : claimRows.find((c) => c.invoiceId === pickedId);

  const crumb = useAccountsCrumb('Accounts', `Bills · ${periodLabel(period).split(' ')[0]}`);
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!picked,
      onPaneClose: () => setPicked(null),
      phoneMenu: (close) => <AccountantMenu door="bills" onClose={close} />,
      title: 'Bills & claims',
      sub: (
        <>
          the billing suite, permission-flattened <Chip kind="outline" small>read-only</Chip>
        </>
      ),
      search: tab === 'bills' ? 'Search a bill number, a patient, an amount…' : 'Search a claim, an insurer, a remittance…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        disabled: tab === 'bills' ? !billRows.length : !claimRows.length,
        onClick: () =>
          tab === 'bills'
            ? exportRows(
                `bills-${period}`,
                ['Bill', 'Patient', 'Date', 'Total', 'Tax', 'Paid', 'Owing', 'Status'],
                billRows.map((b) => [
                  numberOf(b),
                  patientOf(b),
                  b.date ?? '',
                  (num(b.total) / 100).toFixed(2),
                  (num(b.taxAmount ?? b.tax_amount) / 100).toFixed(2),
                  (num(b.amountPaid ?? b.amount_paid) / 100).toFixed(2),
                  (num(b.balance) / 100).toFixed(2),
                  b.status ?? '',
                ]),
              )
            : exportRows(
                `claims-${period}`,
                ['Claim', 'Insurer', 'Filed', 'Approved on', 'Terms (days)', 'Due', 'Amount', 'Paid', 'Outstanding', 'Remittance', 'Status'],
                claimRows.map((c) => [
                  claimNumberOf(c),
                  insurerOf(c),
                  c.filedAt?.slice(0, 10) ?? '',
                  c.approvedAt?.slice(0, 10) ?? '',
                  String(c.termsDays),
                  c.dueOn ?? '',
                  (c.amountMinor / 100).toFixed(2),
                  (c.paidMinor / 100).toFixed(2),
                  (c.outstandingMinor / 100).toFixed(2),
                  c.remittanceRef ?? '',
                  c.status,
                ]),
              ),
      },
      pane: picked ? (
        tab === 'bills' ? (
          <BillPane bill={picked as Invoice} onClose={() => setPicked(null)} />
        ) : (
          <ClaimPane
            claim={picked as Claim}
            insurerClaims={(claims.data ?? []).filter((c) => insurerOf(c) === insurerOf(picked as Claim))}
            onClose={() => setPicked(null)}
            onOpenBill={() => {
              const c = picked as Claim;
              const p = new URLSearchParams(params);
              p.delete('tab');
              setParams(p, { replace: true });
              /* The BILL's month, not the claim's: a claim filed in October can
                 sit on a bill raised in July, and the list only holds one month. */
              const at = c.invoiceDate ?? c.filedAt ?? c.approvedAt;
              if (at) setPeriod(at.slice(0, 7));
              setPicked(c.invoiceId);
            }}
          />
        )
      ) : (
        <Pane
          head={
            <PaneHead
              overline={tab === 'bills' ? 'Selected bill' : 'Selected claim'}
              title="Nothing picked"
              sub={tab === 'bills' ? 'pick a bill to read it' : 'pick a claim to read its clock'}
            />
          }
        >
          <div className="emptystate">
            <p className="t-sec">
              {tab === 'bills'
                ? 'No collect, no refund, no discount — the accountant reads bills, never edits them.'
                : 'Only a recorded remittance clears a claim. Reading it here does not chase it.'}
            </p>
          </div>
        </Pane>
      ),
    },
    [tab, picked, billRows.length, claimRows.length, period, pickedId, query],
  );

  const tableRows: TableRow[] =
    tab === 'bills'
      ? billRows.map((b) => ({
          id: b.id,
          cells: {
            bill: (
              <div style={{ minWidth: 0 }}>
                <p className="t-strong">{numberOf(b)}</p>
                <p className="t-cap mut">{patientOf(b)}</p>
              </div>
            ),
            total: <span className="t-strong num">{amount(num(b.total))}</span>,
            tax: <span className="mut num">{amount(num(b.taxAmount ?? b.tax_amount))}</span>,
            status: (
              <span>
                {statusChip(b.status)}
                {num(b.balance) > 0 && b.status !== 'cancelled' ? (
                  <span className="t-cap mut"> · {amount(num(b.balance))} owing</span>
                ) : null}
              </span>
            ),
          },
        }))
      : claimRows.map((c) => ({
          id: c.invoiceId,
          cells: {
            bill: (
              <div style={{ minWidth: 0 }}>
                <p className="t-strong">{claimNumberOf(c)}</p>
                <p className="t-cap mut">
                  {statusWord(c)} · {insurerOf(c) || 'payer not set'}
                  {c.filedAt ? ` · filed ${c.filedAt.slice(0, 10)}` : ''}
                </p>
              </div>
            ),
            total: <span className="t-strong num">{amount(c.amountMinor)}</span>,
            tax: <ClockChip claim={c} />,
            status: <span className="t-sec mut">{sourceOf(c)}</span>,
          },
        }));

  return (
    <div className="maie desk acc-root">
      <PeriodRow
        period={period}
        onChange={setPeriod}
        branches={summary?.branches}
        trailing={
          <>
            <span className="t-cap mut" style={{ padding: '0 4px' }}>·</span>
            {tab === 'bills' ? (
              <PickerChip
                label={`Sort — ${SORTS[sort]} ▾`}
                ariaLabel="Sort"
                value={sort}
                onChange={setSort}
                options={[
                  { value: 'newest' as const, label: 'Newest first' },
                  { value: 'largest' as const, label: 'Largest first' },
                  { value: 'owing' as const, label: 'Most owing first' },
                ]}
              />
            ) : insurers.length ? (
              <>
                <Chip kind={insurer ? 'outline' : 'brand'} small onClick={() => setInsurer('')}>
                  All insurers
                </Chip>
                {insurers.map((n) => (
                  <Chip
                    key={n}
                    kind={insurer === n ? 'brand' : 'outline'}
                    small
                    onClick={() => setInsurer(insurer === n ? '' : n)}
                  >
                    {n}
                  </Chip>
                ))}
              </>
            ) : null}
          </>
        }
      />

      <div className="dcontent scroll">
        <TabBar
          label="Bills and claims"
          items={[
            { key: 'bills' as const, label: 'Bills' },
            { key: 'claims' as const, label: 'Claims' },
          ]}
          active={tab}
          onSelect={setTab}
        />

        {tab === 'claims' ? (
          <div className="acc-stats">
            <div className="card" style={{ margin: 0 }}>
              <p className="t-ovl mut">Approved, unpaid</p>
              <p className="t-dxl mt8">
                {amount(claimRows.reduce((s, c) => s + outstandingOf(c), 0))}
              </p>
            </div>
            {/* EVERY insurer with money out gets its card, so the cards add up to the
                headline; picking an insurer narrows the cards with the headline. */}
            {insurers
              .filter((n) => !insurer || n === insurer)
              .map((n) => [n, (claims.data ?? []).filter((c) => insurerOf(c) === n).reduce((t, c) => t + outstandingOf(c), 0)] as const)
              .filter(([, owed]) => owed > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([n, owed]) => (
              <div key={n} className="card" style={{ margin: 0 }}>
                <p className="t-ovl mut">{n}</p>
                <p className="t-dxl mt8">{amount(owed)}</p>
              </div>
            ))}
          </div>
        ) : null}

        <DataTable
          caption={tab === 'bills' ? `Bills — ${periodLabel(period)}` : 'Claims'}
          columns={
            tab === 'bills'
              ? [
                  { key: 'bill', label: 'Bill', width: 46 },
                  { key: 'total', label: 'Total', width: 14 },
                  { key: 'tax', label: 'Tax', width: 14 },
                  { key: 'status', label: 'Status', width: 26 },
                ]
              : [
                  { key: 'bill', label: 'Claim', width: 46 },
                  { key: 'total', label: 'Approved', width: 16 },
                  { key: 'tax', label: 'Clock', width: 20 },
                  { key: 'status', label: 'Source', width: 18 },
                ]
          }
          rows={tableRows}
          selectedId={pickedId}
          onSelect={(r) => setPicked(r.id === pickedId ? null : r.id)}
          empty={
            <div className="emptystate">
              {(tab === 'bills' ? bills.isLoading : claims.isLoading) ? (
                <p className="t-sec mut">Reading the ledger…</p>
              ) : (tab === 'bills' ? bills.isError : claims.isError) ? (
                <p className="t-sec mut">It did not come back. Nothing has been changed.</p>
              ) : tab === 'bills' ? (
                <>
                  <p className="es-t">No bills in {periodLabel(period)}</p>
                  <p className="t-sec mut">Pick another month above.</p>
                </>
              ) : (
                <>
                  <p className="es-t">No claims are outstanding</p>
                  <p className="t-sec mut">
                    Every filed claim has been remitted, or nothing has been filed yet.
                  </p>
                </>
              )}
            </div>
          }
        />

        <p className="t-sec mut mt16">
          {tab === 'bills'
            ? 'The same records the desk works, with the verbs removed. Collecting, refunding, discounting and voiding all happen there, under the name of whoever did them.'
            : 'A claim ages from the day it was FILED — that is when its terms start running.'}
        </p>
      </div>
    </div>
  );
}

const SORTS: Record<'newest' | 'largest' | 'owing', string> = {
  newest: 'newest first',
  largest: 'largest first',
  owing: 'most owing first',
};

const numberOf = (b: Invoice) => b.invoiceNumber ?? b.invoice_number ?? '—';
const claimNumberOf = (c: Claim) => c.claimNumber;
const insurerOf = (c: Claim) => c.insurer ?? '';
const outstandingOf = (c: Claim) => c.outstandingMinor;

function patientOf(b: Invoice): string {
  const n = `${b.patient?.firstName ?? ''} ${b.patient?.lastName ?? ''}`.trim();
  return n || 'No patient named';
}

const statusWord = (c: Claim) => c.status.replace(/_/g, ' ');
/** What cleared it, or what it is waiting on. */
const sourceOf = (c: Claim) =>
  c.remittanceRef
    ? `remittance ${c.remittanceRef}`
    : c.status === 'filed'
      ? 'with the insurer'
      : c.status === 'paid'
        ? 'paid · no reference recorded'
        : 'no remittance yet';

function ClockChip({ claim }: { claim: Claim }) {
  if (claim.status === 'paid') return <Chip kind="success" small>settled</Chip>;
  if (claim.status === 'filed' || claim.daysToDue === null) {
    return <span className="t-sec mut">awaiting approval</span>;
  }
  if (claim.daysToDue < 0) return <Chip kind="danger" small>past terms</Chip>;
  return <Chip kind="warn" small>due {claim.daysToDue} d</Chip>;
}

function BillPane({ bill, onClose }: { bill: Invoice; onClose: () => void }) {
  const [history, setHistory] = useState(false);
  const detail = useQuery({
    queryKey: ['accountant', 'bill', bill.id],
    staleTime: 60_000,
    retry: false,
    queryFn: async () => unwrap<InvoiceDetail>((await apiClient.get(`/billing/${bill.id}`)).data),
  });
  const d = detail.data;
  const paid = num(bill.amountPaid ?? bill.amount_paid);
  const owing = num(bill.balance);
  const lines = (d?.items ?? []).map((i) => `${i.description ?? 'Line'} · ${amount(num(i.amount))}`);
  const by = d?.issuedBy ? `${d.issuedBy.firstName ?? ''} ${d.issuedBy.lastName ?? ''}`.trim() : '';
  const stamp = (v?: string) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');
  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline="Selected bill"
          title={numberOf(bill)}
          sub={`${patientOf(bill)} · read-only`}
          wrapTitle
        />
      }
      foot={
        <Button kind="outline" style={{ width: '100%' }} onClick={() => setHistory((v) => !v)}>
          {history ? 'Hide edit history' : 'Full edit history'}
        </Button>
      }
    >
      <Card>
        <p className="t-ovl mut">Lines</p>
        <p className="t-sec sec mt4">
          {detail.isLoading ? 'Reading the bill…' : lines.length ? lines.join('  ·  ') : 'no lines on the bill'}
        </p>
        <p className="t-cap mut mt8">
          {amount(num(bill.total))} billed · {amount(num(bill.taxAmount ?? bill.tax_amount))} tax
          {bill.date ? ` · raised ${bill.date}` : ''}
        </p>
      </Card>

      <Card>
        <p className="t-ovl mut">Payments</p>
        <p className="t-sec sec mt4">
          {paid
            ? `${d?.paymentMethod ? `${d.paymentMethod.replace(/_/g, ' ')} ` : ''}${amount(paid)} collected`
            : 'nothing collected yet'}
          {owing > 0 ? ` · ${amount(owing)} still owing` : ''}
        </p>
        <p className="mt8">{statusChip(bill.status)}</p>
      </Card>

      <Card>
        <p className="t-ovl mut">Edit history</p>
        <p className="t-sec sec mt4">
          {d?.createdAt ? `created${by ? ` by ${by}` : ''} · ${stamp(d.createdAt)}` : 'no history on the bill'}
        </p>
        {history && d?.updatedAt && d.updatedAt !== d.createdAt ? (
          <p className="t-sec sec mt4">last changed · {stamp(d.updatedAt)}</p>
        ) : null}
      </Card>

      <Card kind="accent">
        <p className="t-sec brand">
          No collect, no refund, no discount — the accountant reads bills, never edits them.
        </p>
      </Card>
    </Pane>
  );
}

function ClaimPane({
  claim,
  insurerClaims,
  onClose,
  onOpenBill,
}: {
  claim: Claim;
  insurerClaims: Claim[];
  onClose: () => void;
  onOpenBill: () => void;
}) {
  const owed = outstandingOf(claim);
  const insOwed = insurerClaims.reduce((s, c) => s + outstandingOf(c), 0);
  const day = (v: string | null) => (v ? v.slice(0, 10) : null);
  const late = claim.daysToDue !== null && claim.daysToDue < 0 && claim.status === 'approved';
  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline="Selected claim"
          title={claimNumberOf(claim)}
          sub={`${insurerOf(claim) || 'payer not set'} · ${statusWord(claim)}, ${claim.status === 'paid' ? 'remitted' : 'unpaid'}`}
          wrapTitle
        />
      }
      foot={
        <Button kind="outline" style={{ width: '100%' }} onClick={onOpenBill}>
          Open the bill behind it
        </Button>
      }
    >
      <Card>
        <p className="t-ovl mut">{claim.status === 'filed' ? 'Claimed amount' : 'Approved amount'}</p>
        <p className="t-sec sec mt4">{amount(claim.amountMinor)}</p>
        <p className="t-cap mut mt8">
          {claim.status === 'paid'
            ? `remitted in full${claim.remittanceRef ? ` · ${claim.remittanceRef}` : ''}`
            : owed
              ? `${amount(owed)} of it has not been remitted`
              : 'not yet approved'}
        </p>
      </Card>

      <Card style={late ? { borderColor: 'var(--danger-600)' } : undefined}>
        <p className="t-ovl mut">Clock</p>
        <p className="t-sec sec mt4">
          {claim.status === 'paid'
            ? `settled${claim.paidAt ? ` ${day(claim.paidAt)}` : ''}`
            : claim.daysToDue === null
              ? 'starts when the insurer approves'
              : late
                ? `${-(claim.daysToDue ?? 0)} days past terms`
                : `due in ${claim.daysToDue} day${claim.daysToDue === 1 ? '' : 's'}`}
        </p>
        <p className="t-cap mut mt8">
          terms {claim.termsDays} d · the clock starts at approval
          {claim.dueOn ? ` · due ${claim.dueOn}` : ''}
        </p>
      </Card>

      <Card>
        <p className="t-ovl mut">Insurer</p>
        <p className="t-sec sec mt4">
          {insurerOf(claim) || 'payer not set'} · {insurerClaims.length} claim{insurerClaims.length === 1 ? '' : 's'} ·{' '}
          {amount(insOwed)} approved-unpaid
        </p>
      </Card>

      <Card kind="accent">
        <p className="t-sec brand">
          Nothing here is actioned by the accountant — the desk sends, the manager escalates.
        </p>
      </Card>
    </Pane>
  );
}
